import React, { useState, useCallback } from "react";
import { Box, Button, Typography, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarningIcon from "@mui/icons-material/Warning";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import CampaignDetailsItemsList from "./CampaignDetailsItemsList";
import CampaignDetailsItemsTable from "./CampaignDetailsItemsTable";
import { useGetCampaignItems } from "../../../../../client/hooks/products/promotions.queries";

export default function CampaignDetailsItemsTab({ campaign, setTotalCountItems }) {
    const [items, setItems] = useState([]);
    const [searchAfter, setSearchAfter] = useState(null);
    const [totalItems, setTotalItems] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'table'

    // Hook sempre no topo, enabled: false
    const { refetch } = useGetCampaignItems(
        campaign?.id || null,
        { limit: 20, search_after: searchAfter },
        false,
        campaign?.type
    );

    // Funções utilitárias
    const formatCurrency = (value) => {
        if (value == null) return 'N/A';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'started':
                return 'success';
            case 'pending':
                return 'info';
            case 'finished':
                return 'error';
            default:
                return 'default';
        }
    };

    const fetchItems = useCallback(async (reset = false) => {
        if (!campaign?.id) return;
        setLoading(true);
        setError(null);
        try {
            const params = { limit: 20 };
            if (!reset && searchAfter) params.search_after = searchAfter;
            const { data } = await refetch(params);
            const newItems = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
            setTotalItems(data?.paging?.total || newItems.length || 0);
            setTotalCountItems(data?.paging?.total || newItems.length || 0);
            setSearchAfter(data?.paging?.searchAfter || null);
            setHasMore(!!data?.paging?.searchAfter);
            setItems(prev => reset ? newItems : [...prev, ...newItems]);
            setInitialized(true);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [campaign, searchAfter, refetch]);

    const handleLoadInitial = () => {
        setItems([]);
        setSearchAfter(null);
        setTotalItems(0);
        setHasMore(false);
        setInitialized(false);
        fetchItems(true);
    };    const handleLoadMore = () => {
        if (!loading && hasMore) fetchItems(false);
    };

    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    if (!initialized) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: 'grey.400', mb: 2 }} />
                <Typography variant="subtitle1" color="grey.600" fontWeight={500} sx={{ mb: 2 }}>
                    Visualizar itens da campanha
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleLoadInitial}
                    sx={{ bgcolor: '#3483fa', '&:hover': { bgcolor: '#2968c8' }, textTransform: 'none', fontWeight: 600, px: 4, py: 1.5, borderRadius: 2 }}
                >
                    Carregar Itens
                </Button>
                <Typography variant="body2" color="grey.500" sx={{ mt: 2 }}>
                    Campanha: {campaign.name} (ID: {campaign.id})
                </Typography>
            </Box>
        );
    }
    if (loading && items.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={40} thickness={4} sx={{ color: '#3483fa' }} />
                <Typography variant="body1" sx={{ ml: 2, color: 'grey.600' }}>
                    Carregando itens da campanha {campaign.name}...
                </Typography>
            </Box>
        );
    }
    if (error && items.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light', borderRadius: 2, color: 'error.dark' }}>
                <WarningIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={500}>
                    Erro ao carregar itens da campanha
                </Typography>
                <Typography variant="body2">
                    Por favor, tente novamente mais tarde.
                </Typography>
            </Box>
        );
    }
    if (items.length === 0 && initialized) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                <Typography variant="subtitle1" color="grey.600" fontWeight={500}>
                    {totalItems > 0 ? `${totalItems} itens encontrados nesta campanha` : 'Nenhum item encontrado nesta campanha'}
                </Typography>
            </Box>
        );    }
    return (
        <Box>
            {/* Cabeçalho com seletor de visualização */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                bgcolor: 'grey.25'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.700' }}>
                    {totalItems > 0 ? `${totalItems} itens encontrados` : 'Itens da campanha'}
                </Typography>
                
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            border: '1px solid #e0e0e0',
                            '&.Mui-selected': {
                                bgcolor: '#3483fa',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: '#2968c8',
                                }
                            },
                            '&:hover': {
                                bgcolor: 'rgba(52, 131, 250, 0.04)',
                            }
                        }
                    }}
                >
                    <ToggleButton value="cards" aria-label="visualização em cards">
                        <Tooltip title="Visualização em Cards">
                            <GridViewIcon />
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="visualização em tabela">
                        <Tooltip title="Visualização em Lista">
                            <TableRowsIcon />
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Renderização condicional baseada no modo de visualização */}
            {viewMode === 'cards' ? (
                <CampaignDetailsItemsList
                    items={items}
                    hasMoreItems={hasMore}
                    isLoadingItems={loading}
                    isLoadingMore={loading && items.length > 0}
                    totalItems={totalItems}
                    formatCurrency={formatCurrency}
                    getStatusColor={getStatusColor}
                    onLoadMore={handleLoadMore}
                />
            ) : (
                <CampaignDetailsItemsTable
                    items={items}
                    hasMoreItems={hasMore}
                    isLoadingItems={loading}
                    isLoadingMore={loading && items.length > 0}
                    totalItems={totalItems}
                    formatCurrency={formatCurrency}
                    getStatusColor={getStatusColor}
                    onLoadMore={handleLoadMore}
                />
            )}
        </Box>
    );
}
